import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterConditionOutput } from '@components/ui/AdvancedFilter';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { type AgGridTableProps } from '@shared/components/ui/AgGridTable';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useOrdersWithoutWeight } from '../../hooks/useOrdersWithoutWeight';
import { ErrorState, LoadingState } from '../feedback';
import { OrdersWithoutWeightAgGridTable } from '../display';
import { FiltersSection } from '../interaction';
import { EmptyView, EMPTY_VIEW_ICONS } from '@shared/components/ui/EmptyView';
import { useProcessedFilters } from '../../hooks/useProcessedFilters';

type SortingConfig = NonNullable<AgGridTableProps['sorting']>;

export const Main = () => {
  const { t } = useTranslation('ordersWithoutWeightModule');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // 🎯 Hook useQueryParams para sincronizar con URL
  const {
    params: queryParams,
    updateParam,
    updateParams,
  } = useQueryParams({
    search: { type: 'string', default: '' as string },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
    sortBy: { type: 'string', default: 'id' },
    sortDirection: { type: 'string', default: 'asc' },
  });

  // 🎯 Estados locales sincronizados con URL
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>(
    Array.isArray(queryParams.filters) ? queryParams.filters : [],
  );
  const [searchTerm, setSearchTerm] = useState<string>((queryParams.search ?? '').trim());
  const [sorting, setSorting] = useState<SortingConfig | null>(() => ({
    field: queryParams.sortBy || 'id',
    direction: (queryParams.sortDirection as 'asc' | 'desc') || 'asc',
  }));

  // 🎯 Efectos para sincronizar estados locales con URL
  useEffect(() => {
    const paramSearch = (queryParams.search ?? '').trim();
    setSearchTerm((prev) => (prev === paramSearch ? prev : paramSearch));
  }, [queryParams.search]);

  useEffect(() => {
    const nextFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
    setFilterConditions((prev) => {
      const prevSerialized = JSON.stringify(prev);
      const nextSerialized = JSON.stringify(nextFilters);
      return prevSerialized === nextSerialized ? prev : nextFilters;
    });
  }, [queryParams.filters]);

  useEffect(() => {
    const nextSorting = {
      field: queryParams.sortBy || 'id',
      direction: (queryParams.sortDirection as 'asc' | 'desc') || 'asc',
    };
    setSorting((prev) => {
      if (prev && prev.field === nextSorting.field && prev.direction === nextSorting.direction) {
        return prev;
      }
      return nextSorting;
    });
  }, [queryParams.sortBy, queryParams.sortDirection]);

  // 🎯 Handlers optimizados
  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      setFilterConditions(conditions);
      const currentFilters = Array.isArray(queryParams.filters) ? queryParams.filters : [];
      if (JSON.stringify(currentFilters) !== JSON.stringify(conditions)) {
        updateParam('filters', conditions);
      }
    },
    [updateParam, queryParams.filters],
  );

  const handleSearch = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();
      setSearchTerm((prev) => (prev === trimmedValue ? prev : trimmedValue));
      updateParam('search', trimmedValue);
    },
    [updateParam],
  );

  const handleSortingChange = useCallback(
    (newSorting: SortingConfig | null) => {
      setSorting(newSorting);
      const sortBy = newSorting?.field || 'id';
      const sortDirection = newSorting?.direction || 'asc';

      if (queryParams.sortBy !== sortBy || queryParams.sortDirection !== sortDirection) {
        updateParams({
          sortBy: sortBy === 'id' ? undefined : sortBy,
          sortDirection: sortDirection === 'asc' ? undefined : sortDirection,
        });
      }
    },
    [updateParams, queryParams.sortBy, queryParams.sortDirection],
  );

  const processedFilters = useProcessedFilters(filterConditions, sorting);

  const { orders, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, total } =
    useOrdersWithoutWeight({
      ...processedFilters,
    });

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const ordersViewProps = useMemo(
    () => ({
      orders,
      isLoading: isInitialLoad && isLoading,
      isFetching: !isInitialLoad && isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [orders, isInitialLoad, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  if (error) {
    return <ErrorState error={error} onRetry={refetch} />;
  }

  if (isInitialLoad) {
    return <LoadingState />;
  }

  return (
    <div className="px-6 pt-4">
      <div className="mb-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3 flex-wrap">
            <FiltersSection
              handleSearch={handleSearch}
              onFilterChange={handleFilterChange}
              filterConditions={filterConditions}
              searchTerm={searchTerm}
              sorting={sorting}
            />
          </div>
          <TotalResults total={total} />
        </div>
      </div>

      {orders.length > 0 && (
        <div className="mb-4 mt-4">
          <OrdersWithoutWeightAgGridTable
            {...ordersViewProps}
            sorting={sorting}
            onSortingChange={handleSortingChange}
          />
        </div>
      )}

      {orders.length === 0 && (
        <EmptyView
          type={filterConditions.length > 0 || searchTerm.trim().length > 0 ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.title', 'No hay pedidos sin peso')}
          description={t('empty.description', 'No se encontraron pedidos sin peso registrado.')}
        />
      )}
    </div>
  );
};
